'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { classnames } from '@/utils/classnames/classnames';
import Skeleton from '@/components/atoms/skeleton';
import Overlay from '@/components/atoms/overlay';
import type { AuthDrawerSkeletonProps } from './types';

// EWM-306: transient placeholder shown by `next/dynamic` while a lazily-loaded
// auth drawer chunk downloads on first open. It OWNS the open animation: the
// panel slides in from the right (matching the Drawer's `ease-out duration-100`
// enter) and renders the scrim. Once the chunk lands, the real Drawer mounts
// already-open and at rest (it has no `appear`, so it doesn't re-animate) at the
// same z-999/right-0/width position — so the form fills in place with no second
// slide.
const AuthDrawerSkeleton = ({ headlineKey, widthClassName = 'w-screen max-w-md' }: AuthDrawerSkeletonProps) => {
  const translate = useTranslations();
  const [entered, setEntered] = useState(false);

  // Flip on the next frame so the panel first paints off-screen, then transitions
  // in — a state set within the same paint would skip the transition.
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <>
      <Overlay zIndex="z-345" />
      <div
        role="status"
        aria-busy="true"
        aria-label={translate('auth.drawer-loading')}
        className={classnames(
          'fixed inset-y-0 right-0 z-[999] flex h-full flex-col bg-white shadow-xl transition-transform duration-100 ease-out',
          entered ? 'translate-x-0' : 'translate-x-full',
          widthClassName,
        )}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-5">
          <h2 className="text-primary text-20 font-bold">{translate(headlineKey)}</h2>
          <Skeleton className="block h-6 w-6 rounded-md" />
        </div>
        <div className="grow space-y-5 overflow-y-auto p-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={`auth-drawer-skeleton-row-${index}`} className="space-y-2">
              <Skeleton className="block h-4 w-1/3" />
              <Skeleton className="block h-10 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default AuthDrawerSkeleton;
